import { useMutation, useQuery } from '@apollo/client';
import { JSONContent } from '@tiptap/core';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Content,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Header,
  Heading,
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  SidebarTrigger,
  Skeleton,
  Text,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import {
  DynamicArtifactTemplate,
  dynamicArtifactTemplateSchema,
} from '@eluve/llm-outputs';
import { useCaptureEventOnMount } from '@eluve/posthog-react';
import {
  useIsEluveAdmin,
  useIsTemplatePublisher,
  useIsTenantAdminFromSession,
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { ModelPreset } from './ModelPresetSlider';
import { TemplateBundleWorkspace } from './TemplateBundleWorkspace';
import {
  docToDynamicArtifact,
  dynamicArtifactToDoc,
  makeDefaultSectionsDoc,
} from './prompt-editor/templateSectionsDoc';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

/**
 * The section editor can only represent text blocks (and one level of
 * groups). Templates built with other block types must not be re-saved
 * here — the unrepresentable blocks would be silently dropped.
 */
const templateHasUnsupportedBlocks = (
  template: DynamicArtifactTemplate,
): boolean =>
  template.blocks.some((block) =>
    block.type === 'group'
      ? block.blocks.some((child) => child.type !== 'text')
      : block.type !== 'text',
  );

export const getTemplateBundleForEditQuery = graphql(`
  query getTemplateBundleForEdit($outputTemplateId: uuid!, $tenantId: uuid!) {
    tenantTemplateBundles(
      where: {
        tenantId: { _eq: $tenantId }
        llm_model_prompt_template: {
          outputTemplateId: { _eq: $outputTemplateId }
          isBundle: { _eq: true }
        }
      }
    ) {
      __typename
      tenantId
      llmModelPromptTemplateId
      isEnabled
    }
    llmOutputTemplatesByPk(id: $outputTemplateId) {
      __typename
      id
      tenantId
      createdBy
      name
      description
      variants(orderBy: { variantNumber: DESC }) {
        __typename
        outputTemplateId
        variantNumber
        template
        isDraft
        isCurrentDefault
        variantNotes
        createdAt
      }
    }
    llmModelPromptTemplates(
      where: {
        outputTemplateId: { _eq: $outputTemplateId }
        isBundle: { _eq: true }
      }
    ) {
      __typename
      id
      modelArgsId
    }
  }
`);

type TemplateVersion = {
  variantNumber: number;
  isDraft: boolean;
  isCurrentDefault: boolean;
  template: DynamicArtifactTemplate;
};

const parseVariantTemplate = (template: unknown): DynamicArtifactTemplate => {
  const parsed = dynamicArtifactTemplateSchema.safeParse(template);
  return parsed.success ? parsed.data : { name: '', blocks: [] };
};

const setTenantBundleEnabledMutation = graphql(`
  mutation setTenantBundleEnabled(
    $tenantId: uuid!
    $llmModelPromptTemplateId: uuid!
    $isEnabled: Boolean!
  ) {
    insertTenantTemplateBundlesOne(
      object: {
        tenantId: $tenantId
        llmModelPromptTemplateId: $llmModelPromptTemplateId
        isEnabled: $isEnabled
      }
      onConflict: {
        constraint: tenant_template_bundles_pkey
        updateColumns: [isEnabled]
      }
    ) {
      __typename
      tenantId
      llmModelPromptTemplateId
      isEnabled
    }
  }
`);

// Name-agnostic fingerprint of a sections document, for dirty-checking.
const serializeDocBlocks = (doc: JSONContent): string => {
  try {
    return JSON.stringify(docToDynamicArtifact({ doc, name: '' }).blocks);
  } catch {
    return JSON.stringify(doc);
  }
};

/**
 * Edit experience for a saved template (ENG-4547). The current state of
 * a version loads into the shared authoring workspace; "Save draft"
 * keeps a private working copy, "Publish version" adds a new version
 * and makes it active, and any older version can be re-activated to
 * control what generation uses.
 */
export const EditTemplateBundlePage: React.FC = () => {
  useCaptureEventOnMount('page_view_edit_template_bundle');

  const { outputTemplateId } = useParams<{ outputTemplateId: string }>();
  const apiClient = useApiClient();
  const tenantId = useTenantIdFromParams();
  const toast = useToast();
  const navigate = useNavigate();

  const { data, loading, refetch } = useQuery(getTemplateBundleForEditQuery, {
    variables: { outputTemplateId: outputTemplateId!, tenantId: tenantId! },
    skip: !outputTemplateId || !tenantId,
  });

  const template = data?.llmOutputTemplatesByPk;
  const bundleModelArgsId = data?.llmModelPromptTemplates?.[0]?.modelArgsId;
  const bundleAssignment = data?.tenantTemplateBundles?.[0];

  const currentUserId = useUserIdFromSession();
  const isTenantAdmin = useIsTenantAdminFromSession();
  const isEluveAdmin = useIsEluveAdmin();
  const isTemplatePublisher = useIsTemplatePublisher();

  // Enable/disable a template's org-wide availability. Backend requires the
  // tenant-admin role + publisher flag (or an Eluve admin); the UI mirrors
  // that and runs the mutation under the matching role.
  const canManageEnabled =
    Boolean(bundleAssignment) &&
    (isEluveAdmin || (isTenantAdmin && isTemplatePublisher));
  const [setBundleEnabled, { loading: isTogglingEnabled }] = useMutation(
    setTenantBundleEnabledMutation,
  );
  const handleToggleEnabled = async () => {
    if (!tenantId || !bundleAssignment) {
      return;
    }
    try {
      await setBundleEnabled({
        variables: {
          tenantId,
          llmModelPromptTemplateId: bundleAssignment.llmModelPromptTemplateId,
          isEnabled: !bundleAssignment.isEnabled,
        },
        context: isEluveAdmin
          ? eluveAdminHasuraContext
          : { headers: { 'x-hasura-role': 'tenant-admin' } },
      });
      toast.success(
        bundleAssignment.isEnabled
          ? 'Disabled for the organization'
          : 'Enabled for the organization',
      );
      await refetch();
    } catch (error) {
      toast.error('Failed to update availability', {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  // Edits in place are reserved for the owner; admins may override (with a
  // warning). Everyone else — other tenants, or same-tenant non-owners —
  // gets read-only + "Save a copy". The server enforces this too.
  const isOtherTenant = Boolean(template) && template?.tenantId !== tenantId;
  const isOwner = Boolean(template) && template?.createdBy === currentUserId;
  const isReadOnly =
    isOtherTenant ||
    (Boolean(template) && !isOwner && !isTenantAdmin && !isEluveAdmin);
  const isAdminEditingOthers = !isReadOnly && Boolean(template) && !isOwner;

  const versions = useMemo<TemplateVersion[]>(() => {
    return (template?.variants ?? []).map((variant) => ({
      variantNumber: Number(variant.variantNumber),
      isDraft: variant.isDraft,
      isCurrentDefault: variant.isCurrentDefault,
      template: parseVariantTemplate(variant.template),
    }));
  }, [template]);

  const activeVersion = versions.find((v) => v.isCurrentDefault);
  const draftVersion = versions.find((v) => v.isDraft);

  // Default to the draft (the only editable version), else the active one
  const [pickedVariantNumber, setPickedVariantNumber] = useState<number | null>(
    null,
  );
  const selectedVersion =
    versions.find((v) => v.variantNumber === pickedVariantNumber) ??
    draftVersion ??
    activeVersion ??
    versions[0];
  // Only the draft is editable; published versions are read-only
  const isViewingDraft = selectedVersion?.isDraft ?? false;
  const isEditorReadOnly = isReadOnly || !isViewingDraft;

  const [templateName, setTemplateName] = useState<string | null>(null);
  const effectiveName = templateName ?? template?.name ?? '';
  // Null until edited, overlaying the server value (same as the name)
  const [description, setDescription] = useState<string | null>(null);
  const effectiveDescription = description ?? template?.description ?? '';
  const [selectedPreset, setSelectedPreset] = useState<ModelPreset | null>(
    null,
  );

  const hasUnsupportedBlocks =
    selectedVersion !== undefined &&
    templateHasUnsupportedBlocks(selectedVersion.template);

  const initialDoc = useMemo<JSONContent>(() => {
    return selectedVersion
      ? dynamicArtifactToDoc(selectedVersion.template)
      : makeDefaultSectionsDoc();
  }, [selectedVersion]);
  // Dirty-check baseline. Compared semantically (extracted blocks) because
  // the editor emits onUpdate on mount and ProseMirror normalizes the doc,
  // which would otherwise read as false "unsaved changes".
  const baselineBlocks = useMemo(
    () => serializeDocBlocks(initialDoc),
    [initialDoc],
  );
  const docRef = useRef<JSONContent>(initialDoc);
  // Switching versions remounts the editor with the new document, so
  // the working copy must follow
  const lastVariantNumber = useRef(selectedVersion?.variantNumber);
  if (lastVariantNumber.current !== selectedVersion?.variantNumber) {
    lastVariantNumber.current = selectedVersion?.variantNumber;
    docRef.current = initialDoc;
  }

  const [isSaving, setIsSaving] = useState(false);
  const {
    markDirty,
    markClean,
    dialog: unsavedChangesDialog,
  } = useUnsavedChangesGuard(!isEditorReadOnly);

  // The slider reports its initial selection once on mount; only a later
  // (user-driven) preset change should dirty the page so the navigation guard
  // protects it.
  const presetNotified = useRef(false);
  const handlePresetSelected = useCallback(
    (preset: ModelPreset) => {
      setSelectedPreset(preset);
      if (presetNotified.current) {
        markDirty();
      } else {
        presetNotified.current = true;
      }
    },
    [markDirty],
  );

  const handleSaveVersion = async (isDraft: boolean) => {
    if (!tenantId || !outputTemplateId || hasUnsupportedBlocks) {
      return;
    }
    const name = effectiveName.trim();
    if (!name) {
      toast.error('Give the template a name first');
      return;
    }

    setIsSaving(true);
    try {
      const response = await apiClient.llm.saveTemplateBundleVersion({
        params: { tenantId, outputTemplateId },
        body: {
          outputTemplate: docToDynamicArtifact({ doc: docRef.current, name }),
          isDraft,
          name,
          description: effectiveDescription.trim() || undefined,
          modelPresetId: selectedPreset?.id,
          basedOnVariantNumber: activeVersion?.variantNumber,
        },
      });

      if (response.status === 201) {
        toast.success(isDraft ? 'Draft saved' : 'Version published');
        markClean();
        // Refetch before switching the selected version: the editor is keyed
        // by variant number and reloads from server data on switch, so the
        // fresh save must be in cache before the key flips (else it remounts
        // with the pre-save content).
        await refetch();
        setPickedVariantNumber(response.body.variantNumber);
      } else if (response.status === 409) {
        toast.error(
          response.body.message ?? 'A newer version is already active',
        );
        await refetch();
      } else if (response.status === 422) {
        toast.error(response.body.message ?? 'Failed to save');
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save', {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Forks the current editor contents into a brand-new template owned by
  // the active tenant and user (a fresh bundle + output template), then
  // lands on its edit page. Used to make an editable copy of a read-only
  // (e.g. Eluve-provided) template.
  const handleSaveCopy = async () => {
    if (!tenantId || hasUnsupportedBlocks) {
      return;
    }
    const baseName = effectiveName.trim() || template?.name || 'Untitled';
    const copyName =
      baseName === template?.name ? `Copy of ${baseName}` : baseName;

    setIsSaving(true);
    try {
      const response = await apiClient.llm.createTemplateBundle({
        params: { tenantId },
        body: {
          name: copyName,
          description: effectiveDescription.trim() || undefined,
          outputTemplate: docToDynamicArtifact({
            doc: docRef.current,
            name: copyName,
          }),
          modelPresetId: selectedPreset?.id,
          isDraft: false,
        },
      });

      if (response.status === 201) {
        toast.success('Saved a copy');
        // Clear before navigating so the guard doesn't catch its own
        // post-save navigation
        markClean();
        navigate(`../bundles/${response.body.outputTemplateId}`);
      } else if (response.status === 422) {
        toast.error(response.body.message ?? 'Failed to save a copy');
      } else {
        toast.error('Failed to save a copy');
      }
    } catch (error) {
      toast.error('Failed to save a copy', {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!tenantId || !outputTemplateId || !selectedVersion) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await apiClient.llm.activateTemplateBundleVersion({
        params: {
          tenantId,
          outputTemplateId,
          variantNumber: String(selectedVersion.variantNumber),
        },
        body: {},
      });

      if (response.status === 200) {
        toast.success(`Version ${selectedVersion.variantNumber} is now active`);
        await refetch();
      } else if (response.status === 422) {
        toast.error(response.body.message ?? 'Failed to set active version');
      } else {
        toast.error('Failed to set active version');
      }
    } catch (error) {
      toast.error('Failed to set active version', {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const showSetActive =
    !isReadOnly &&
    selectedVersion !== undefined &&
    !selectedVersion.isCurrentDefault &&
    !selectedVersion.isDraft &&
    versions.length > 1;

  const handleEditDraft = () => {
    if (!draftVersion) {
      return;
    }
    markClean();
    setPickedVariantNumber(draftVersion.variantNumber);
  };

  const overflowMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="More actions">
          <Icon name="Ellipsis" size="md" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {showSetActive && (
          <DropdownMenuItem disabled={isSaving} onClick={handleActivate}>
            Set as Active
          </DropdownMenuItem>
        )}
        {canManageEnabled && (
          <DropdownMenuItem
            disabled={isTogglingEnabled}
            onClick={handleToggleEnabled}
          >
            Enabled for Organization
            {bundleAssignment?.isEnabled && (
              <Icon name="Check" size="sm" className="ml-auto" />
            )}
          </DropdownMenuItem>
        )}
        {(showSetActive || canManageEnabled) && <DropdownMenuSeparator />}
        <DropdownMenuItem
          disabled={isSaving || loading || hasUnsupportedBlocks}
          onClick={handleSaveCopy}
        >
          Save a Copy
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      <Header>
        <SidebarTrigger />
        <Link to=".." aria-label="Back to templates">
          <Button variant="ghost" size="sm">
            <Icon name="ArrowLeft" size="md" />
          </Button>
        </Link>
        <div className="my-2 py-2">
          <Heading>Edit Template</Heading>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {versions.length > 0 && selectedVersion && (
            <>
              <Select
                value={String(selectedVersion.variantNumber)}
                onValueChange={(value) => {
                  // Switching versions remounts the editor with the picked
                  // version's content, discarding any in-editor edits; clear
                  // the dirty flag to match so leaving afterward doesn't
                  // falsely prompt.
                  markClean();
                  setPickedVariantNumber(Number(value));
                }}
              >
                <SelectTrigger className="w-fit" aria-label="Template version">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {versions.map((version) => (
                    <SelectItem
                      key={version.variantNumber}
                      value={String(version.variantNumber)}
                    >
                      Version {version.variantNumber}
                      {version.isCurrentDefault
                        ? ' · Active'
                        : version.isDraft
                          ? ' · Draft'
                          : ' · Inactive'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Same-variant override so twMerge drops the baked-in
                  self-stretch (which spans the full header height) */}
              <Separator
                orientation="vertical"
                className="mx-1 h-6 data-[orientation=vertical]:self-center"
              />
            </>
          )}
          {isReadOnly ? (
            <Button
              disabled={isSaving || loading || hasUnsupportedBlocks}
              onClick={handleSaveCopy}
            >
              {isSaving ? 'Saving…' : 'Save a Copy'}
            </Button>
          ) : isViewingDraft ? (
            <>
              <Button
                variant="outline"
                disabled={isSaving || loading || hasUnsupportedBlocks}
                onClick={() => handleSaveVersion(true)}
              >
                Save Draft
              </Button>
              <Button
                disabled={isSaving || loading || hasUnsupportedBlocks}
                onClick={() => handleSaveVersion(false)}
              >
                {isSaving ? 'Saving…' : 'Publish Version'}
              </Button>
              {overflowMenu}
            </>
          ) : draftVersion ? (
            <>
              <Button variant="outline" onClick={handleEditDraft}>
                Edit Draft
              </Button>
              {overflowMenu}
            </>
          ) : (
            <>
              <Button
                disabled={isSaving || loading || hasUnsupportedBlocks}
                onClick={() => handleSaveVersion(true)}
              >
                {isSaving ? 'Creating…' : 'Create Draft'}
              </Button>
              {overflowMenu}
            </>
          )}
        </div>
      </Header>
      <Content>
        {/* Match the patient pages' content inset */}
        <div className="py-3 md:p-8">
          {loading ? (
            <div className="flex flex-col gap-4">
              <Skeleton className="h-10 w-1/3" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : !template ? (
            <Text color="tertiary">Template not found.</Text>
          ) : (
            <>
              {hasUnsupportedBlocks && (
                <div className="mb-4 rounded-md border border-border-primary bg-bg-secondary p-3">
                  <Text size="sm" color="secondary">
                    This template uses advanced blocks that this editor can't
                    represent yet, so saving is disabled — re-saving here would
                    drop those blocks.
                  </Text>
                </div>
              )}
              {isReadOnly && (
                <div className="mb-4 rounded-md border border-border-warning-subtle bg-bg-warning-subtle p-3 text-text-warning-subtle">
                  <Text size="sm" color="inherit">
                    {isOtherTenant
                      ? "This template is provided by Eluve and can't be changed here. Feel free to explore and tweak it — use "
                      : "This template is owned by another member, so it can't be changed here. Use "}
                    <span className="font-medium">Save a copy</span> to create
                    your own editable version for this organization.
                  </Text>
                </div>
              )}
              {isAdminEditingOthers && isViewingDraft && (
                <div className="mb-4 rounded-md border border-border-primary bg-bg-secondary p-3">
                  <Text size="sm" color="secondary">
                    You're editing a template owned by another member. As an
                    admin you can publish changes — it stays owned by them.
                  </Text>
                </div>
              )}
              <TemplateBundleWorkspace
                templateName={effectiveName}
                description={effectiveDescription}
                onDescriptionChange={(value) => {
                  setDescription(value);
                  markDirty();
                }}
                onTemplateNameChange={(name) => {
                  setTemplateName(name);
                  markDirty();
                }}
                initialDoc={initialDoc}
                editorKey={selectedVersion?.variantNumber}
                onDocChange={(doc) => {
                  docRef.current = doc;
                  if (serializeDocBlocks(doc) !== baselineBlocks) {
                    markDirty();
                  }
                }}
                onPresetSelected={handlePresetSelected}
                initialModelArgsId={bundleModelArgsId ?? undefined}
                readOnly={isEditorReadOnly}
              />
            </>
          )}
        </div>
      </Content>
      {unsavedChangesDialog}
    </>
  );
};
